import { ChangeEvent } from 'react';
import { SearchInput } from '@design-system/components/searchinput';
import { DropDown } from '@design-system/components/dropDown/dropDown';
import {
  DropDownFilter,
  FilterOption,
} from '@design-system/components/dropDown/dropDownFilter';
import { PortalUserRole } from '../../model/PortalUserList.types';
import {
  PortalUserLinkedFilter,
  PortalUserSortField,
  PortalUserStatusFilter,
} from '../../view-model/PortalUserList.filterEngine';
import * as styles from './SearchBar.css';

type Props = {
  search: string;
  role: PortalUserRole | null;
  status: PortalUserStatusFilter;
  linked: PortalUserLinkedFilter;
  sort: PortalUserSortField;
  onSearchChange: (value: string) => void;
  onRoleChange: (value: PortalUserRole | null) => void;
  onStatusChange: (value: PortalUserStatusFilter) => void;
  onLinkedChange: (value: PortalUserLinkedFilter) => void;
  onSortChange: (value: PortalUserSortField) => void;
};

const ROLE_OPTIONS: FilterOption<number>[] = [
  { value: 1, label: 'Super Admin' },
  { value: 2, label: 'Admin' },
  { value: 3, label: 'Creator' },
  { value: 4, label: 'Content Editor' },
];

const STATUS_OPTIONS: FilterOption<PortalUserStatusFilter>[] = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const LINKED_OPTIONS: FilterOption<PortalUserLinkedFilter>[] = [
  { value: 'linked', label: 'Linked to creator' },
  { value: 'unlinked', label: 'Unlinked' },
];

const SORT_OPTIONS = [
  { value: 'id' as PortalUserSortField, label: 'Newest first' },
  { value: 'name' as PortalUserSortField, label: 'Name (A → Z)' },
];

const isPortalUserRole = (value: number): value is PortalUserRole =>
  value === 1 || value === 2 || value === 3 || value === 4;

export const SearchBar = (p: Props) => {
  const sortLabel = SORT_OPTIONS.find(
    (option) => option.value === p.sort,
  )?.label;

  return (
    <div className={styles.wrapper}>
      <SearchInput
        placeholder="Search by name, email, or id"
        value={p.search}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          p.onSearchChange(event.target.value)
        }
        onClear={() => p.onSearchChange('')}
      />

      <div className={styles.filterRow}>
        <DropDownFilter<number>
          label="Role"
          value={p.role ?? undefined}
          options={ROLE_OPTIONS}
          onSelect={(value) =>
            p.onRoleChange(
              value !== undefined && isPortalUserRole(value) ? value : null,
            )
          }
        />
        <DropDownFilter<PortalUserStatusFilter>
          label="Status"
          value={p.status === 'all' ? undefined : p.status}
          options={STATUS_OPTIONS}
          onSelect={(value) => p.onStatusChange(value ?? 'all')}
        />
        <DropDownFilter<PortalUserLinkedFilter>
          label="Linked creator"
          value={p.linked === 'all' ? undefined : p.linked}
          options={LINKED_OPTIONS}
          onSelect={(value) => p.onLinkedChange(value ?? 'all')}
        />
        <DropDown
          label={`Sort: ${sortLabel}`}
          options={SORT_OPTIONS}
          selectedValue={p.sort}
          onClick={(value) => p.onSortChange(value as PortalUserSortField)}
        />
      </div>
    </div>
  );
};
